import { useCallback, useSyncExternalStore } from 'react';
import * as Cookies from '#util/cookies.ts';

// <https://developer.mozilla.org/en-US/docs/Web/API/CookieStore>
const supportsCookieStore = 'cookieStore' in window;

const CUSTOM_COOKIE_EVENT_TYPE = 'use-cookie';

/**
 * Custom hook to retrieve and store cookies for our application.
 *
 * Writes always dispatch a custom event, and subscribers always listen for it. We can't rely on
 * `cookieStore.change` to fire for legacy `document.cookie` writes — WebKit's event-firing logic
 * compares cookie expiry vs. creation time after CFNetwork floors both to whole seconds. Our
 * delete path (`Cookies.setItem(key, '', 0)`) writes `expires=<now>`, so after flooring the diff
 * is 0 and affected WebKit builds suppress the change event. Safari 18.6 (Sentry WEBCLIENT-1KC)
 * was observed hitting this, causing `useSyncExternalStore` to miss auth-token clears and
 * triggering a 401 retry storm. See <https://bugs.webkit.org/show_bug.cgi?id=285628> (Jan 2025)
 * for the original fix; related fixes continued landing through Aug 2025
 * (<https://bugs.webkit.org/show_bug.cgi?id=288847>,
 * <https://bugs.webkit.org/show_bug.cgi?id=297053>). The
 * `cookieStore.change` listener remains as a defensive signal for cookie changes that bypass
 * this hook (e.g. server `Set-Cookie` responses) in browsers that support it.
 */
export function useCookie(key: string) {
  // The external store has one source of truth. React uses this reader to obtain a reactive
  // snapshot; async operations can also call it directly when they need the value at commit time.
  const getCurrent = useCallback(() => {
    return Cookies.getItem(key);
  }, [key]);

  const subscribe = useCallback((callback: () => void) => {
    window.addEventListener(CUSTOM_COOKIE_EVENT_TYPE, callback);
    if (supportsCookieStore) {
      window.cookieStore.addEventListener('change', callback);
    }
    return () => {
      window.removeEventListener(CUSTOM_COOKIE_EVENT_TYPE, callback);
      if (supportsCookieStore) {
        window.cookieStore.removeEventListener('change', callback);
      }
    };
  }, []);

  const cookie = useSyncExternalStore(subscribe, getCurrent);

  const updateCookie = (value: string, numberOfDays?: number) => {
    Cookies.setItem(key, value, numberOfDays);
    window.dispatchEvent(new Event(CUSTOM_COOKIE_EVENT_TYPE));
  };

  const deleteCookie = () => {
    Cookies.setItem(key, '', 0);
    window.dispatchEvent(new Event(CUSTOM_COOKIE_EVENT_TYPE));
  };

  return [cookie, updateCookie, deleteCookie, getCurrent] as const;
}
